import { cn } from "@/lib/utils";
import { pathwayOptionLetter } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { Star } from "lucide-react";

export interface PathwayOptionPickerProps {
  /** How many paths the level offers. Renders nothing below two. */
  count: number;
  /** 0-based. */
  selected: number;
  onSelect?: (option: number) => void;
  readOnly?: boolean;
  /**
   * Per option, whether it can be chosen right now. An unavailable option is
   * drawn but inert. Absent means every option is available.
   */
  isOptionAvailable?: (option: number) => boolean;
  /**
   * Fires with the option under the pointer, and with nothing once it
   * leaves, so a host can preview a path before it is chosen.
   */
  onHover?: (option: number | undefined) => void;
  /**
   * A choice is resolving: none can be pressed. The pills keep their shape;
   * the level card shows the spinner in its header.
   */
  loading?: boolean;
  /** The option the solver recommends; drawn with a star. */
  recommended?: number;
  className?: string;
}

/**
 * The "Path A" / "Path B" pills under a level card: one per path, the
 * chosen one filled gold-on-green. On phones the row is padded so the touch
 * target still clears 44px.
 */
export const PathwayOptionPicker = ({
  count,
  selected,
  onSelect,
  readOnly = false,
  isOptionAvailable,
  onHover,
  loading = false,
  recommended,
  className,
}: PathwayOptionPickerProps) => {
  const { t } = useTranslation("evolutions");
  if (count < 2) return null;
  const interactive = !readOnly && !!onSelect;

  return (
    <div
      role="radiogroup"
      className={cn(
        "flex flex-wrap items-center gap-2 max-touch-device:py-1",
        className,
      )}
    >
      {Array.from({ length: count }, (_, option) => {
        const isSelected = option === selected;
        const available = isOptionAvailable?.(option) ?? true;
        const pressable = interactive && available && !loading;
        const label = t("pathway.path", { path: pathwayOptionLetter(option) });
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={!pressable}
            onClick={() => onSelect?.(option)}
            onMouseEnter={available ? () => onHover?.(option) : undefined}
            onMouseLeave={available ? () => onHover?.(undefined) : undefined}
            className={cn(
              // transition-none: the global button transition would fade the
              // old choice out, which reads as a flash of the wrong path.
              "inline-flex h-6 items-center justify-center gap-1.5 rounded-full px-2.5 text-xs font-semibold whitespace-nowrap transition-none outline-none max-touch-device:h-7",
              "focus-visible:ring-2 focus-visible:ring-white/40",
              isSelected
                ? "bg-evolution text-evolution-secondary"
                : "border border-edge-strongest text-foreground-muted",
              pressable
                ? "cursor-pointer"
                : "cursor-default disabled:opacity-100",
              // Unavailable reads as such; a read-only picker keeps full
              // opacity because nothing there is meant to be pressed.
              interactive && !available && "opacity-40",
              pressable && !isSelected && "hover:border-foreground-muted",
            )}
          >
            {option === recommended && (
              <Star
                className="size-3 fill-current"
                aria-label={t("pathway.recommended")}
              />
            )}
            {label}
          </button>
        );
      })}
    </div>
  );
};
